import AddIcon from "@mui/icons-material/Add";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import DialogContent from "@mui/joy/DialogContent";
import DialogTitle from "@mui/joy/DialogTitle";
import Drawer from "@mui/joy/Drawer";
import Grid from "@mui/joy/Grid";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import ModalClose from "@mui/joy/ModalClose";
import Skeleton from "@mui/joy/Skeleton";
import Stack from "@mui/joy/Stack";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabList from "@mui/joy/TabList";
import TabPanel from "@mui/joy/TabPanel";
import Tabs from "@mui/joy/Tabs";
import Typography from "@mui/joy/Typography";
import React from "react";
import { SubmitHandler } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import { BundleContext } from "../../../App";
import { ClinicalEvent } from "../../../models/clinical_event";
import { Note, NoteId, ValidationState } from "../../../models/note";
import { Person, PersonId } from "../../../models/person";
import { Coding } from "../../../models/structuration";
import { PatientView } from "../../../models/view";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import FactsTimeline from "../../../shared/components/ui/FactsTimeline/FactsTimeline";
import {
  NoteQuartzClientContext,
  PersonQuartzClientContext,
} from "../../../shared/services/api/contexts";
import { AddNote } from "../../notes/components/AddNote";
import { ListOfNotes } from "../../notes/components/ListOfNotes";
import NoteFilters from "../../notes/components/NoteFilters";
import { UploadDocument } from "../../notes/components/UploadDocument";
import CancerPanel from "../components/CancerPanel";
import DataConsistency from "../components/DataConsistency";
import { DecisionSupport } from "../components/DecisionSupport";
import { ListAppointments } from "../components/ListAppointments";
import { PatientDetails } from "../components/PatientDetails";
import { PatientForm } from "../components/PatientForm";
import { QuestionAnswering } from "../components/QuestionAnswering";

type TabKey = "overview" | "notes" | "medications" | "dataIssues" | "addNote" | "uploadDoc";

export const PatientById = () => {
  // Contexts
  const { showSnackbar } = useSnackbar();
  const noteQuartzClient = React.useContext(NoteQuartzClientContext);
  const personQuartzClient = React.useContext(PersonQuartzClientContext);
  const [, setBundleId] = React.useContext(BundleContext);
  const location = useLocation();

  // States
  const [currentTab, setCurrentTab] = React.useState<TabKey>("overview");
  const [noteLoading, setNoteLoading] = React.useState<boolean>(true);
  const [person, setPerson] = React.useState<Person | null>(null);
  const [personId, setPersonId] = React.useState<PersonId | undefined | null>(
    location.pathname.split("/")[2] as PersonId
  );
  const [patientView, setPatientView] = React.useState<PatientView | null>(null);
  const [validationState, setValidationState] = React.useState<ValidationState>("all");
  const [noteTypeCode, setNoteTypeCode] = React.useState<string | null>(null);
  const [searchByCoding, setSearchByCoding] = React.useState<Coding | null>(null);
  const [notes, setNotes] = React.useState<Note[] | undefined>([]);
  const [modalModifyPatient, setModalModifyPatient] = React.useState<boolean>(false);
  const [refreshTime, setRefreshTime] = React.useState<number>(new Date().getTime());

  // Constants
  const count = 100;
  const offset = 0;

  const refresh = () => {
    setRefreshTime(new Date().getTime());
  };

  const updatePatient = async (person_id: PersonId, patientInputs: Person) => {
    try {
      const personUpdate = await personQuartzClient.putPersonById(person_id, patientInputs);
      setPerson(personUpdate);
      setModalModifyPatient(false);
      showSnackbar({
        text: "Patient updated successfully",
        color: "success",
      });
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const onModifyPatient: SubmitHandler<Person> = async (data) => {
    data.person_id && updatePatient(data.person_id, data);
  };

  const deleteNotes = async (todelete: Array<NoteId>) => {
    try {
      await noteQuartzClient.deleteNotes(todelete);
      refresh();
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  const groupEventByName = (events: ClinicalEvent[]) => {
    const grouped: { [key: string]: ClinicalEvent[] } = {};
    events.forEach((event) => {
      if (grouped[event.name]) {
        grouped[event.name].push(event);
      } else {
        grouped[event.name] = [event];
      }
    });
    return grouped;
  };

  const reloadPatientView = async () => {
    if (!personId) return;
    try {
      const view = await noteQuartzClient.getPatientView(personId, "all");
      setPatientView(view);
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  };

  React.useEffect(() => {
    if (!personId) return;
    (async () => {
      try {
        const [patient, view] = await Promise.all([
          personQuartzClient.getPersonById(personId),
          noteQuartzClient.getPatientView(personId, "all"),
        ]);
        setPerson(patient);
        setPatientView(view);
      } catch (e: any) {
        showSnackbar({ text: e.message, color: "danger" });
      }
    })();
  }, [personId, personQuartzClient, noteQuartzClient, showSnackbar]);

  React.useEffect(() => {
    const getNotesOfTheCurrentPatient = async () => {
      if (!personId) return;
      try {
        const bundle = await noteQuartzClient.listNotes(
          count,
          offset,
          validationState,
          noteTypeCode,
          personId,
          searchByCoding
        );
        setBundleId(bundle.id ? bundle.id : null);
        const listOfNotes = bundle.entry as Note[];

        const sortedNotes = [...listOfNotes].sort((a, b) => {
          // If both person_ids are null or both are not null, then sort by note_datetime
          if (a.note_datetime && b.note_datetime) {
            return a.note_datetime - b.note_datetime; // Older notes first (ascending order)
          }
          // If note_datetime is not available for both, keep the original order
          return 0;
        });
        setNotes(sortedNotes);
      } catch (e: any) {
        setPersonId(null);
        showSnackbar({ text: e.message, color: "danger" });
      } finally {
        setNoteLoading(false);
      }
    };

    getNotesOfTheCurrentPatient();
  }, [
    refreshTime,
    noteTypeCode,
    validationState,
    searchByCoding,
    personId,
    noteQuartzClient,
    count,
    offset,
    setBundleId,
    showSnackbar,
  ]);

  return (
    <Box>
      <Grid container spacing={2} sx={{ flexGrow: 1, p: 4 }}>
        <Grid xs={8}>
          {person ? (
            <PatientDetails person={person} />
          ) : (
            <Skeleton variant="rectangular" width={400} height={44} />
          )}
        </Grid>
        <Grid xs={4}>
          <Box justifyContent="flex-end" display="flex">
            <Button
              onClick={() => {
                setModalModifyPatient(!modalModifyPatient);
              }}
              variant="solid"
              startDecorator={<EditIcon fontSize="small" />}
            >
              Edit patient details
            </Button>
          </Box>
        </Grid>
      </Grid>
      {person && (
        <Drawer
          open={modalModifyPatient}
          onClose={() => setModalModifyPatient(false)}
          anchor="right"
        >
          <ModalClose />
          <DialogTitle>Edit patient details</DialogTitle>
          <DialogContent>
            <PatientForm patient={person} onSubmit={onModifyPatient} />
          </DialogContent>
        </Drawer>
      )}

      <Tabs
        value={currentTab}
        onChange={(_, newValue) => newValue && setCurrentTab(newValue as TabKey)}
        sx={{
          bgcolor: "transparent",
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: { sm: -100, md: -110 },
            bgcolor: "background.body",
          }}
        >
          <TabList
            tabFlex={1}
            size="sm"
            sx={{
              justifyContent: "left",
              [`&& .${tabClasses.root}`]: {
                fontWeight: "600",
                flex: "initial",
                color: "text.tertiary",
                [`&.${tabClasses.selected}`]: {
                  bgcolor: "transparent",
                  color: "text.primary",
                  "&::after": {
                    height: "2px",
                    bgcolor: "primary.500",
                  },
                },
              },
            }}
          >
            <Tab sx={{ borderRadius: "6px 6px 0 0", minWidth: "120px" }} value="overview">
              <ListItemDecorator>
                <SummarizeOutlinedIcon fontSize="small" />
              </ListItemDecorator>
              Overview
            </Tab>
            <Tab sx={{ borderRadius: "6px 6px 0 0", minWidth: "100px" }} value="notes">
              <ListItemDecorator>
                <ArticleOutlinedIcon fontSize="small" />
              </ListItemDecorator>
              Notes
            </Tab>
            <Tab sx={{ borderRadius: "6px 6px 0 0", minWidth: "140px" }} value="medications">
              <ListItemDecorator>
                <MedicationOutlinedIcon fontSize="small" />
              </ListItemDecorator>
              Medications
            </Tab>
            {patientView && patientView.data_inconsistencies.length > 0 && (
              <Tab
                sx={{ borderRadius: "6px 6px 0 0", minWidth: "140px" }}
                indicatorInset
                value="dataIssues"
              >
                <ListItemDecorator>
                  <WarningAmberRoundedIcon fontSize="small" />
                </ListItemDecorator>
                Data issues ({patientView.data_inconsistencies.length})
              </Tab>
            )}
            <Tab
              sx={{
                borderRadius: "6px 6px 0 0",
                backgroundColor: "#DED2AE",
                mr: 1,
                ml: 1,
                minWidth: "150px",
              }}
              indicatorInset
              value="addNote"
            >
              <ListItemDecorator>
                <AddIcon fontSize="small" />
              </ListItemDecorator>
              Add a note
            </Tab>
            <Tab
              sx={{ borderRadius: "6px 6px 0 0", backgroundColor: "#DED2AE", minWidth: "180px" }}
              indicatorInset
              value="uploadDoc"
            >
              <ListItemDecorator>
                <PublishIcon fontSize="small" />
              </ListItemDecorator>
              Upload documents
            </Tab>
          </TabList>
        </Box>
        <TabPanel value="overview">
          {!patientView && (
            <Stack
              spacing={2}
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ pt: "30vh" }}
            >
              <CircularProgress />
              <Typography>Loading patient's data...</Typography>
            </Stack>
          )}
          {patientView && personId && <QuestionAnswering personIds={[personId]} />}
          <Grid container spacing={2}>
            <Grid lg={9} md={12}>
              {patientView && Object.keys(patientView.cancer_facts).length > 0 && (
                <CancerPanel patientView={patientView} />
              )}
            </Grid>
            <Grid md={3}>{personId && <ListAppointments patientId={personId} />}</Grid>
          </Grid>
          <Grid container spacing={2} sx={{ p: 1 }}>
            {patientView?.cds_response?.cards && patientView.cds_response.cards.length > 0 && (
              <Grid xs={12}>
                <DecisionSupport cards={patientView.cds_response.cards} />
              </Grid>
            )}
          </Grid>
        </TabPanel>
        <TabPanel sx={{ p: 3 }} value="notes">
          <Grid xs={12}>
            {!noteLoading ? (
              <Stack direction="column" spacing={2}>
                {notes && notes.length !== 0 && (
                  <NoteFilters
                    defaultStatus={validationState}
                    defaultNoteType={noteTypeCode}
                    searchByCoding={searchByCoding}
                    onStatusChange={setValidationState}
                    onNoteTypeChange={setNoteTypeCode}
                    onSearch={setSearchByCoding}
                  />
                )}
                {notes && notes.length !== 0 ? (
                  <ListOfNotes deleteNotes={deleteNotes} notes={notes ?? []} />
                ) : (
                  <Box
                    sx={{
                      mt: "150px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Typography level="h3">
                        There are no notes linked to this patient yet.
                      </Typography>
                      <Stack sx={{ mt: 2 }} direction="row" spacing={1} justifyContent="center">
                        <Link to={"/add-document"}>
                          <Button startDecorator={<ImportExportIcon />}>Upload document</Button>
                        </Link>
                        <Link to={"/add-note"}>
                          <Button startDecorator={<AddIcon />}>Add a manual note</Button>
                        </Link>
                      </Stack>
                    </div>
                  </Box>
                )}
              </Stack>
            ) : (
              <Skeleton variant="rectangular" width="100%" height="100px" />
            )}
          </Grid>
        </TabPanel>
        <TabPanel sx={{ p: 3 }} value="medications">
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            <Grid xs={12}>
              {patientView ? (
                <>
                  {patientView.rx_facts && Object.keys(patientView.rx_facts).length > 0 ? (
                    <FactsTimeline eventsByName={groupEventByName(patientView.rx_facts)} />
                  ) : (
                    <Typography level="h3">No treatment found</Typography>
                  )}
                </>
              ) : (
                <Skeleton variant="rectangular" width={44} height={44} />
              )}
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel sx={{ p: 3 }} value="dataIssues">
          {patientView ? (
            <DataConsistency
              dataInconsistencies={patientView.data_inconsistencies}
              onDelete={() => {
                reloadPatientView();
                setCurrentTab("dataIssues"); // stay here
              }}
            />
          ) : (
            <CircularProgress />
          )}
        </TabPanel>
        <TabPanel sx={{ p: 3 }} value="addNote">
          <AddNote person={person} />
        </TabPanel>
        <TabPanel sx={{ p: 3 }} value="uploadDoc">
          <UploadDocument personId={person?.person_id} />
        </TabPanel>
      </Tabs>
    </Box>
  );
};
